import { Button, LoadingIndicator } from '@cfa/react-components';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import { EventSummary, OrderSummary, Section, SectionDivider, SectionHeader, Totals } from '../components';
import DeliveryTipsSection from '../components/DeliveryTips/DeliveryTipsSection';
import { selectAppLoading } from '../reducers';
import { actions as guestActions, selectGuestDetails } from '../reducers/guest';
import {
  actions as orderActions,
  selectCartItems,
  selectDeliveryTipIsDisplayed,
  selectDeliveryTipLoading,
  selectDestination,
  selectDestinationDetails,
  selectEventDetails,
  selectFormattedTotals,
  selectOrderIsSubmitStatus,
  selectOriginalTip,
  selectPaymentSubmitted,
  selectSelectedCustomTipValue,
  selectSubTotalAmount,
} from '../reducers/order';
import { actions as paymentActions } from '../reducers/payment';
import { logAmplitudeEvent } from '../services/amplitude';

export class ReviewSummary extends Component {
  componentDidMount() {
    window.scrollTo(0, 0);
  }

  render() {
    const {
      guestDetails,
      destination,
      eventDetails,
      destinationDetails,
      formattedTotals,
      cartItems,
      loading,
      deliveryTipIsDisplayed,
      deliveryTipLoading,
      orderIsSubmitStatus,
      throwAlreadySubmittedFatalError,
      addDeliveryTip,
      subTotalAmount,
      selectedCustomTipValue,
      originalTip,
      resubmitOrder,
    } = this.props;

    if (!loading && !deliveryTipIsDisplayed) {
      throwAlreadySubmittedFatalError();
    }
    logAmplitudeEvent('[My Payment] Stored Payment Review Summary Rendered', {
      guestDetails,
      destination,
      eventDetails,
    });

    return (
      <StyledReviewSummary>
        <Section>
          {loading ? (
            <LoadingIndicator variant="page" size="lg" className="loading" data-testid="loading-indicator" />
          ) : (
            <>
              <SectionHeader className="header">
                {guestDetails.first}, thank you for your order!
              </SectionHeader>
              <EventSummary
                destination={destination}
                guestDetails={guestDetails}
                eventDetails={eventDetails}
                destinationDetails={destinationDetails}
                orderIsSubmitStatus={orderIsSubmitStatus}
              />
              <OrderSummary cartItems={cartItems} />
              <SectionDivider />
              <DeliveryTipsSection
                addDeliveryTip={addDeliveryTip}
                subTotal={subTotalAmount}
                selectedCustomTipValue={selectedCustomTipValue}
                isUpdate={originalTip > 0}
              />
              <Totals
                subTotal={formattedTotals.subTotal}
                tax={formattedTotals.tax}
                taxAndTotal={formattedTotals.taxAndTotal}
                deliveryTipIsDisplayed={deliveryTipIsDisplayed}
                deliveryTipAmount={formattedTotals.deliveryTipAmount}
                deliveryTipLoading={deliveryTipLoading}
              />
              <div className="tip-button">
                <Button
                  className="button-text"
                  variant="filled"
                  onClick={() => resubmitOrder()}
                  disabled={
                    selectedCustomTipValue < 0 ||
                    selectedCustomTipValue > 99999 ||
                    selectedCustomTipValue === originalTip
                  }
                >
                  {originalTip === 0 ? 'Add Optional Tip' : 'Update Tip'}
                </Button>
              </div>
            </>
          )}
        </Section>
      </StyledReviewSummary>
    );
  }
}

const StyledReviewSummary = styled.div`
  & .tip-button {
    display: flex;
    justify-content: flex-end;
  }
  & .header {
    margin-top: 20px;
  }
  & .loading {
    margin: 20px auto 0;
  }
  @media (max-width: 500px) {
    & .tip-button button,
    .tip-button a {
      width: 100%;
    }
  }
`;

ReviewSummary.propTypes = {
  destination: PropTypes.string.isRequired,
  guestDetails: PropTypes.objectOf(PropTypes.string),
  eventDetails: PropTypes.shape({
    time: PropTypes.string,
    guestCount: PropTypes.number,
    paperGoods: PropTypes.bool,
    specialInstructions: PropTypes.string,
    date: PropTypes.object,
  }),
  destinationDetails: PropTypes.objectOf(PropTypes.string),
  cartItems: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  match: PropTypes.shape({
    path: PropTypes.string,
    url: PropTypes.string,
    isExact: PropTypes.bool,
    params: PropTypes.object,
  }).isRequired,
  formattedTotals: PropTypes.objectOf(PropTypes.string),
  deliveryTipIsDisplayed: PropTypes.bool,
  deliveryTipLoading: PropTypes.bool,
  orderIsSubmitStatus: PropTypes.bool,
  throwAlreadySubmittedFatalError: PropTypes.func,
  addDeliveryTip: PropTypes.func.isRequired,
  subTotalAmount: PropTypes.number,
  selectedCustomTipValue: PropTypes.number,
  originalTip: PropTypes.number,
  resubmitOrder: PropTypes.func.isRequired,
};

ReviewSummary.defaultProps = {
  guestDetails: {},
  destinationDetails: {},
  eventDetails: {},
  cartItems: [{}],
  formattedTotals: {},
  loading: false,
  deliveryTipIsDisplayed: false,
  deliveryTipLoading: false,
  orderIsSubmitStatus: false,
  subTotalAmount: 0,
  selectedCustomTipValue: 0,
  originalTip: 0,
  throwAlreadySubmittedFatalError: () => {},
};

/* v8 ignore start */
function mapStateToProps(state) {
  return {
    guestDetails: selectGuestDetails(state),
    eventDetails: selectEventDetails(state),
    destination: selectDestination(state),
    loading: selectAppLoading(state),
    cartItems: selectCartItems(state),
    destinationDetails: selectDestinationDetails(state),
    submitted: selectPaymentSubmitted(state),
    formattedTotals: selectFormattedTotals(state),
    deliveryTipIsDisplayed: selectDeliveryTipIsDisplayed(state),
    deliveryTipLoading: selectDeliveryTipLoading(state),
    orderIsSubmitStatus: selectOrderIsSubmitStatus(state),
    subTotalAmount: selectSubTotalAmount(state),
    selectedCustomTipValue: selectSelectedCustomTipValue(state),
    originalTip: selectOriginalTip(state),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ ...guestActions, ...paymentActions, ...orderActions }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ReviewSummary);
